import { useI18n } from "features/translation/context";
import { _, _any } from "i18n";
import type {
	ColorEnum,
	FileResponse,
	GameClipIntervalRequest,
	GamePlayerItemRequest,
	GameStatsExportRequest,
	GameTeamPutColorsRequest,
	GameTeamSheetCompositionPlayersResponse,
	GameTeamSheetGameTeamPlayersSearchResponse,
	GameTeamSheetPlayersResponse,
	UserWidgetSettingsResponse,
	saveUserWidgetSettingsRequestBody,
} from "libs/apiClient";
import client from "libs/client";
import { Link, useParams } from "react-router";
import "style/gamePage.css";
import { GamePageSkeleton } from "common/src/gamePage/components/Skeletons/GamePageSkeleton";
import { type EventFilterValue, type GameEventDataExportRequest, type GameEventFilterItemPostRequest, NavOptions } from "common/src/gamePage/types";
import { useCallback, useEffect, useMemo, useState } from "react";
import { APIResponse } from "types";
import { useToast } from "ui-kit";
import ForbiddenPage from "./errorPages/ForbiddenPage";
import NotFoundPage from "./errorPages/NotFoundPage";
import { useGamePage } from "common/src/gamePage/use-game-page";
import { AdminGameVideoUploadActions } from "features/videoUploads/AdminGameVideoUploadActions";
import { UserDownloadActivityKeys, UserDownloadActivityTags } from "../../../../assets/js/types";

interface Colors {
	shirt?: ColorEnum;
	shorts?: ColorEnum;
}

const SWAP_HOME_AWAY_MAPPING_ELIGIBILITY_QUERY_KEY = "swapHomeAwayMappingEligibility";

export function GamePage() {
	const { addSuccessToast, addErrorToast } = useToast();
	const { locale } = useI18n();
	const { id } = useParams();

	const adminURL = import.meta.env.VITE_FE_URL;

	const [selectedTab, setSelectedTab] = useState<NavOptions>(NavOptions.FullGame);
	const [selectedClip, setSelectedClip] = useState(0);
	const [clipsPagination, setClipsPagination] = useState({ page: 1, limit: 10 });
	const [newShareSlug, setNewShareSlug] = useState<{ slug: string; id: number }>({ id: 0, slug: "" });

	const {
		data: gameData,
		isLoading: gameDataIsLoading,
		error,
	} = client.getGamePage.useQuery({ id: Number(id) }, { queryKey: ["gameData"], enabled: !!id });

	const { data: gameDataLazy, isLoading: gameDataLazyIsLoading } = client.getGamePageLazy.useQuery(
		{ id: Number(id) },
		{ queryKey: ["gameDataLazy"], enabled: !!gameData?.data?.game?.id }
	);

	const { data: gamePlayerStat, isLoading: gamePlayerStatIsLoading } = client.getGamePlayerStat.useQuery(
		{ id: Number(id) },
		{
			queryKey: ["gamePlayerStat", id],
			enabled: !!gameData?.data?.game?.id && !!gameData?.data?.permissions.showPlayerStats,
		},
	);

	const { data: gameTeamStat, isLoading: gameTeamStatIsLoading } = client.getGameTeamStat.useQuery(
		{ id: Number(id) },
		{
			queryKey: ["gameTeamStat", id],
			enabled: gameData?.data?.game?.id != null && gameData.data.permissions.showTeamStats,
		},
	);

	const { data: gameClips, isLoading: isClipsLoading } = client.getGameClips.useQuery(
		{ filters: [{ field: "gameId", type: "exact", value: id }], limit: clipsPagination.limit, page: clipsPagination.page },
		{
			queryKey: ["gameClips"],
			enabled: !!gameData?.data.permissions.useClips && !!gameData?.data?.game?.id,
		}
	);

	const { data: selectedClipData, isLoading: isSelectedClipLoading } = client.getGameClip.useQuery(
		{ id: selectedClip },
		{ enabled: !!selectedClip }
	);

	const { data: gameEventFiltersData, isLoading: gameEventFiltersIsLoading } = client.getGameEventFilters.useQuery({
		queryKey: ["gameEventFilters"],
		enabled: !!gameData?.data?.permissions.showEvents && !!gameData?.data?.game?.id,
	});

	const colorsMutation = client.putGameTeamColors.useMutation({
		onSuccess: () => {
			addSuccessToast("colors has been changed");
			client.queryClient.invalidateQueries({
				predicate: (query) => query.queryKey.includes("gameData"),
			});
		},
		onError: (error) => {
			addErrorToast(error.message);
		},
	});

	const scoreMutation = client.putGameScore.useMutation({
		onSuccess: () => {
			addSuccessToast("score has been changed");
			client.queryClient.invalidateQueries({
				predicate: (query) => query.queryKey.includes("gameData"),
			});
		},
		onError: (error) => {
			addErrorToast(error.message);
		},
	});

	const editClipMutation = client.putGameClip.useMutation({
		onSuccess: () => {
			addSuccessToast("Clip edited");
			client.queryClient.invalidateQueries({
				predicate: (query) => query.queryKey.includes("gameClips"),
			});
		},
		onError: (error) => {
			addErrorToast(error.message);
		},
	});

	const deleteClipMutation = client.deleteGameClip.useMutation({
		onSuccess: () => {
			addSuccessToast("Clip deleted");
			client.queryClient.invalidateQueries({
				predicate: (query) => query.queryKey.includes("gameClips"),
			});
		},
		onError: (error) => {
			addErrorToast(error.message);
		},
	});

	const createHighlightMutation = client.postGameHighlight.useMutation({
		onSuccess: () => {
			addSuccessToast("Custom event created");
			client.queryClient.invalidateQueries({
				predicate: (query) => query.queryKey.includes("gameDataLazy"),
			});
		},
		onError: (error) => {
			addErrorToast(error.message);
		},
	});

	const editHighlightMutation = client.patchGameHighlight.useMutation({
		onSuccess: () => {
			addSuccessToast("Edit Successful");
			client.queryClient.invalidateQueries({
				predicate: (query) => query.queryKey.includes("gameDataLazy"),
			});
		},
		onError: (error) => {
			addErrorToast(error.message);
		},
	});

	const deleteHighlightMutation = client.deleteGameHighlight.useMutation({
		onSuccess: () => {
			addSuccessToast("Event Deleted");
			client.queryClient.invalidateQueries({
				predicate: (query) => query.queryKey.includes("gameDataLazy"),
			});
		},
		onError: (error) => {
			addErrorToast(error.message);
		},
	});

	const createClipMutation = client.postGameClip.useMutation({
		onSuccess: () => {
			addSuccessToast("Clip created");
			client.queryClient.invalidateQueries({
				predicate: (query) => query.queryKey.includes("gameClips"),
			});
		},
		onError: (error) => {
			addErrorToast(error.message);
		},
	});

	const handleCreateClip = (intervals: GameClipIntervalRequest[], name: string, description?: string) => {
		createClipMutation.mutate({
			gameId: Number(id),
			body: {
				description: description ?? "",
				intervals: intervals,
				name: name,
				tags: null,
			},
		});
	};

	const handleDeleteClip = (id: number) => {
		deleteClipMutation.mutate({ id: id });
	};

	const handleEditClip = (id: number, name: string, description?: string) => {
		editClipMutation.mutate({
			id: id,
			body: {
				description: description ?? "",
				name: name,
			},
		});
	};

	const handleDeleteHighlight = (id: number) => {
		deleteHighlightMutation.mutate({ id: id ?? 0 });
	};

	const handleEditHighlight = (note: string, timestamp: number, title: string, id?: number) => {
		editHighlightMutation.mutate({
			id: id ?? 0,
			body: {
				note: note,
				timestamp: timestamp,
				title: title,
			},
		});
	};

	const handleCreateHighlight = (note: string, timestamp: number, title: string) => {
		createHighlightMutation.mutate({
			gameId: Number(id),
			body: {
				note: note,
				timestamp: timestamp,
				title: title,
			},
		});
	};

	const handleChangeColors = (id: number, colors: Colors) => {
		colorsMutation.mutate({
			id: id,
			body: {
				colorShirt: colors.shirt,
				colorShort: colors.shorts,
			},
		});
	};

	const handleChangeScore = (id: number, scores: { homeTeam?: number; awayTeam?: number }) => {
		scoreMutation.mutate({
			id: id,
			body: {
				homeTeamScore: scores.homeTeam ?? 0,
				awayTeamScore: scores.awayTeam ?? 0,
			},
		});
	};

	const handleChangeNavOptions = useCallback((navOption: NavOptions) => {
		setSelectedTab(navOption);
	}, []);

	const editTeamSheetMutation = client.putGameTeamSheet.useMutation({
		onSuccess: () => {
			addSuccessToast("success");
			client.queryClient.invalidateQueries({
				predicate: (query) => query.queryKey.includes("gameData"),
			});
		},
		onError: (error) => {
			addErrorToast(error.message);
		},
	});

	const handleTeamSheetEdit = async (
		id: number,
		squad: GamePlayerItemRequest[],
		substitutes: GamePlayerItemRequest[],
		colors: GameTeamPutColorsRequest,
		saveAnyway: boolean,
		gender?: string | null,
	): Promise<boolean> => {
		try {
			await editTeamSheetMutation.mutateAsync({
				id: id,
				body: {
					squad: squad,
					substitutes: substitutes,
					colors: colors,
					saveAnyway: saveAnyway,
					gender: gender ?? null,
				},
			});
			return true;
		} catch (error) {
			return false;
		}
	};

	const loadCompositionPlayers = async (teamId: number, composition: string): Promise<GameTeamSheetCompositionPlayersResponse | null> => {
		try {
			const response = await client.getGameTeamSheetCompositionPlayers.query({ id: teamId, composition });
			addSuccessToast(_("form.club.match_sheet.players.autoload.success"));

			return response.data;
		} catch (error) {
			return null;
		}
	};

	const searchPlayers = async (teamId: number, filter: string): Promise<GameTeamSheetGameTeamPlayersSearchResponse | null> => {
		try {
			const response = await client.getGameTeamPlayersSearch.query({ id: teamId, filter: filter });

			return response.data ?? null;
		} catch (error) {
			return null;
		}
	};

	const loadClubPlayers = async (teamId: number): Promise<GameTeamSheetPlayersResponse | null> => {
		try {
			const response = await client.getGameTeamSheetClubPlayers.query({ id: teamId });

			return response.data ?? null;
		} catch (error) {
			return null;
		}
	};

	const updateGameLeftTeamMutation = client.putUpdateGameLeftTeam.useMutation({
		onSuccess: () => {
			addSuccessToast("Game left team updated");
			client.queryClient.invalidateQueries({
				predicate: (query) =>
					query.queryKey.includes("gameData") || query.queryKey.includes(SWAP_HOME_AWAY_MAPPING_ELIGIBILITY_QUERY_KEY),
			});
		},
		onError: (error) => {
			addErrorToast(error.message);
		},
	});

	const handleLeftSideStart = (homeTeamStartsOnLeft: boolean | null): void => {
		updateGameLeftTeamMutation.mutate({
			gameId: Number(id),
			body: { homeTeamStartsOnLeft },
		});
	};

	const descriptionMutation = client.putGameDescription.useMutation({
		onSuccess: () => {
			addSuccessToast("changed");
			client.queryClient.invalidateQueries({
				predicate: (query) => query.queryKey.includes("gameData"),
			});
		},
		onError: (error) => {
			addErrorToast(error.message);
		},
	});

	const shareLinkMutation = client.shareClipLink.useMutation({
		onSuccess: (data, variables) => {
			setNewShareSlug({ id: variables.id, slug: `${adminURL}/clip/${data?.data.data?.clip_link}` });
			addSuccessToast(_("message.clips.share.success"));
			client.queryClient.invalidateQueries({
				predicate: (query) => query.queryKey.includes("gameClips"),
			});
		},
		onError: () => {
			addErrorToast(_any("message.clips.share.error"));
		},
	});

	const handleGenerateShareLink = (
		id: number,
		description: string | undefined,
		password: string | undefined,
		expireDate: string | undefined,
		isPasswordUpdated: boolean
	) => {
		shareLinkMutation.mutate({
			id: id,
			body: {
				description: description ? description : "",
				expireDate: expireDate ? expireDate : "",
				password: password ? password : "",
				isPasswordUpdated: isPasswordUpdated,
			},
		});
	};

	const handleChangeDescription = (id: number, description: string | null) => {
		descriptionMutation.mutate({
			id: id,
			body: {
				description: description,
			},
		});
	};

	const analysisApprovalMutation = client.putGameAnalysisApproval.useMutation({
		onSuccess: () => {
			addSuccessToast("Success");
			client.queryClient.invalidateQueries({
				predicate: (query) => query.queryKey.includes("gameData"),
			});
		},
		onError: (error) => {
			addErrorToast(error.message);
		},
	});

	const handleChangeAnalysisApproval = async (approvalStatus: "approved" | "disapproved"): Promise<boolean> => {
		try {
			await analysisApprovalMutation.mutateAsync({
				id: Number(id),
				body: {
					approvalStatus: approvalStatus,
				},
			});

			return true;
		} catch (error) {
			return false;
		}
	};

	const gameResyncMutation = client.putGameResync.useMutation({
		onSuccess: () => {
			addSuccessToast("Success");
			client.queryClient.invalidateQueries({
				predicate: (query) =>
					query.queryKey.includes("gameData") || query.queryKey.includes(SWAP_HOME_AWAY_MAPPING_ELIGIBILITY_QUERY_KEY),
			});
		},
		onError: (error) => {
			addErrorToast(error.message);
		},
	});

	const handleGameResync = (resyncType: string | null, syncLocally: boolean | null, resyncAfter: string | null, skipReview: boolean) => {
		gameResyncMutation.mutate({
			id: Number(id),
			body: {
				resyncAfter: resyncAfter,
				resyncType: resyncType,
				syncLocally: syncLocally,
				skipReview: skipReview,
			},
		});
	};

	const { data: swapHomeAwayMappingEligibility } = client.getGameHomeAwayMappingSwapEligibility.useQuery(
		{ id: Number(id) },
		{
			queryKey: [SWAP_HOME_AWAY_MAPPING_ELIGIBILITY_QUERY_KEY, Number(id)],
			enabled: id != null && id !== "" && selectedTab === NavOptions.Admin,
		}
	);

	const swapHomeAwayMappingMutation = client.swapGameHomeAwayMapping.useMutation({
		onSuccess: () => {
			addSuccessToast(_("game.swapHomeAwayMapping.toast.requested"));
			client.queryClient.invalidateQueries({
				predicate: (query) =>
					query.queryKey.includes("gameData") || query.queryKey.includes(SWAP_HOME_AWAY_MAPPING_ELIGIBILITY_QUERY_KEY),
			});
		},
		onError: (error) => {
			addErrorToast(error.message);
		},
	});

	const handleSwapHomeAwayMapping = () => {
		if (window.confirm(_("game.swapHomeAwayMapping.confirm")) === false) {
			return;
		}

		swapHomeAwayMappingMutation.mutate({ id: Number(id) });
	};

	const handleAbortGameResync = () => {
		gameResyncMutation.mutate({
			id: Number(id),
			body: {
				abortResync: true,
			},
		});
	};

	const invokeDownload = (fileResponse: FileResponse) => {
		const binaryData = atob(fileResponse.file);
		const byteArray = new Uint8Array(binaryData.length);
		for (let i = 0; i < binaryData.length; i++) {
			byteArray[i] = binaryData.charCodeAt(i);
		}

		const blob = new Blob([byteArray], { type: fileResponse.fileType });

		const url = window.URL.createObjectURL(blob);
		const a = document.createElement("a");
		a.href = url;
		a.download = fileResponse.fileName;
		document.body.appendChild(a);
		a.click();
		document.body.removeChild(a);
		window.URL.revokeObjectURL(url);
	};

	const gameStatsExportMutation = client.postGameStatsExport.useMutation({
		onSuccess: (data) => {
			if (!data || !data.data.fileName || !data.data.fileName) {
				addErrorToast("Error");
				return;
			}

			invokeDownload(data.data);
		},
		onError: (error) => {
			addErrorToast(error.message);
		},
	});

	const handleGameStatsExport = (gameStatsExportRequest: GameStatsExportRequest) => {
		if (gameData?.data) {
			gameStatsExportMutation.mutate({
				id: gameData.data.game.id,
				body: gameStatsExportRequest,
			});
		}
	};

	const gameEventDataExportMutation = client.postGameEventDataExport.useMutation({
		onError: (error) => {
			addErrorToast(error.message);
		},
	});

	const handleGameEventDataExport = async (gameEventDataExportRequest: GameEventDataExportRequest) => {
		if (!gameData?.data) {
			return;
		}

		const data = await gameEventDataExportMutation.mutateAsync({
			id: gameData.data.game.id,
			body: gameEventDataExportRequest,
		});

		if (!data?.data?.fileName) {
			addErrorToast("Error");
			throw new Error("Event export failed");
		}

		invokeDownload(data.data);
	};

	const deleteGameEventFilterMutation = client.deleteGameEventFilter.useMutation({
		onSuccess: () => {
			addSuccessToast("Filter Deleted");
			client.queryClient.invalidateQueries({
				predicate: (query) => query.queryKey.includes("gameEventFilters"),
			});
		},
		onError: (error) => {
			addErrorToast(error.message);
		},
	});

	const handleDeleteGameEventFilter = (id: number) => {
		deleteGameEventFilterMutation.mutate({ id: id });
	};

	const createGameEventFilterMutation = client.postGameEventFilter.useMutation({
		onSuccess: () => {
			addSuccessToast("Filter saved");
			client.queryClient.invalidateQueries({
				predicate: (query) => query.queryKey.includes("gameEventFilters"),
			});
		},
		onError: (error) => {
			addErrorToast(error.message);
		},
	});

	const handleCreateGameEventFilter = (name: string, filterValues: EventFilterValue[]) => {
		createGameEventFilterMutation.mutate({
			body: {
				name: name,
				filters: filterValues.map(
					(fv) =>
						({
							key: fv.key,
							value: fv.value,
						}) as GameEventFilterItemPostRequest
				),
			},
		});
	};

	const gameTeamSheetDataExportMutation = client.postGameTeamSheetExport.useMutation({
		onSuccess: (data) => {
			if (!data || !data.data.fileName || !data.data.fileName) {
				addErrorToast("Error");
				return;
			}

			invokeDownload(data.data);
		},
		onError: (error) => {
			addErrorToast(error.message);
		},
	});

	const handleTeamSheetDataExport = (type: string) => {
		if (gameData?.data) {
			gameTeamSheetDataExportMutation.mutate({
				id: gameData.data.game.id,
				exportType: type,
			});
		}
	};

	const toastMessage = (type: string, message: string) => {
		if (type === "success") {
			addSuccessToast(message);
		}
		if (type === "error") {
			addErrorToast(message);
		}
	};

	const { data: userWidgetSettings, isLoading: isFetchingUserWidgetSettings } = client.getUserWidgetSettings.useQuery({
		queryKey: ["userWidgetSettings"],
		enabled: selectedTab === NavOptions.DataInsights,
	});

	const saveUserWidgetSettingsMutation = client.saveUserWidgetSettings.useMutation({
		onSuccess: () => {
			addSuccessToast(_("gamePage.dataInsights.label.widgetSettingSaved"));
			client.queryClient.invalidateQueries({
				predicate: (query) => query.queryKey.includes("userWidgetSettings"),
			});
		},
		onError: (error) => {
			addErrorToast(error.message);
		},
	});

	const handleSaveUserWidgetSettings = async (settings: saveUserWidgetSettingsRequestBody): Promise<void> => {
		await saveUserWidgetSettingsMutation.mutateAsync({ body: settings });
	};

	const storeDownloadActivityMutation = client.logUserActivity.useMutation({
		onSuccess: () => { },
		onError: () => { },
	});

	const storeDownloadVideoActivity = (tag: UserDownloadActivityTags, gameId: number) => {
		let dataTag = UserDownloadActivityKeys.GAME_ID;

		if (tag === UserDownloadActivityTags.GAME_CLIP) {
			dataTag = UserDownloadActivityKeys.CLIP_ID;
		}

		storeDownloadActivityMutation.mutate({
			body: {
				path: tag,
				//@ts-ignore
				isDownload: true,
				//@ts-ignore
				data: {
					[dataTag]: gameId,
				},
			},
		});
	};
	// memoize the result of gameId so it doesn't get recalculated on every render
	const gameId = useMemo(() => Number(id), [id]);
	const {
		data: physicalAnalysisReport,
		isLoading: physicalAnalysisReportIsLoading,
		error: physicalAnalysisReportError,
	} = client.adminGetGameAnalysisPhysical.useQuery(
		{ gameId: gameId },
		{
			queryKey: ["gameAnalysisPhysical", gameId],
			enabled: (
				selectedTab === NavOptions.PhysicalAnalysis || selectedTab === NavOptions.Admin
			),
			retry: false,
		}
	);

	const handlePostPhysicalAnalysisReportUrls = async (urls: string[]) => {
		const response = await client.postGameAnalysisPhysicalVideoUrl.mutation({
			body: {
				gameId: gameId,
				physicalMetricsVideoUrls: urls,
			},
		});

		const data = response?.data as
			| { status?: string; message?: string; gameId?: number }
			| undefined;

		if (data && "status" in data) {
			if (data.status === APIResponse.SUCCESS) {
				return;
			}
			const message =
				(typeof data.message === "string" && data.message) ||
				_("gamePage.physicalMetrics.unknownErrorOccured");
			throw new Error(message);
		}

		// 200 OK with a plain `GameAnalysisPhysicalResponse` body (no API envelope)
		if (data && typeof data.gameId === "number") {
			return;
		}

		const message =
			(data && typeof data.message === "string" && data.message) ||
			_("gamePage.physicalMetrics.unknownErrorOccured");
		throw new Error(message);
	};

	const gamePage = useGamePage({
		toastMessage: toastMessage,
		isAdmin: true,
		domainURL: adminURL,
		gameData: gameData?.data,
		gameDataLazy: gameDataLazy?.data,
		locale: locale,
		currentNavOption: selectedTab,
		handleChangeColors: handleChangeColors,
		handleChangeScore,
		handleChangeNavOptions,
		highlightsHandlers: { create: handleCreateHighlight, edit: handleEditHighlight, delete: handleDeleteHighlight },
		handleGameStatsExport,
		handleGameEventDataExport,
		//onFavoriteClick,
		//favoriteDisabled,
		//isFavorite,
		clips: {
			create: handleCreateClip,
			edit: handleEditClip,
			delete: handleDeleteClip,
			selectClip: (id: number) => setSelectedClip(id),
			selectedClip: selectedClipData?.data,
			data: gameClips?.data,
			pagination: clipsPagination,
			isLoading: isClipsLoading,
			isSelectedClipLoading: isSelectedClipLoading,
			onPaginationChange: (pagination: typeof clipsPagination) => setClipsPagination(pagination),
			generateShareLink: handleGenerateShareLink,
			newShareSlug: newShareSlug,
		},
		gameEventFilters: {
			create: handleCreateGameEventFilter,
			delete: handleDeleteGameEventFilter,
			isLoading: gameEventFiltersIsLoading,
			data: gameEventFiltersData?.data,
		},
		handleTeamSheetEdit,
		loadCompositionPlayers,
		searchPlayers,
		loadClubPlayers,
		handleChangeDescription,
		allowEditDescription: true,
		handleTeamSheetDataExport,
		handleLeftSideStart,
		handleChangeAnalysisApproval,
		handleAbortGameResync,
		handleGameResync,
		canSwapHomeAwayMapping: swapHomeAwayMappingEligibility?.data.canSwap,
		isSwappingHomeAwayMapping: swapHomeAwayMappingMutation.isLoading,
		handleSwapHomeAwayMapping,
		swapped: swapHomeAwayMappingEligibility?.data.swapped,
		keepMappingAllowed: swapHomeAwayMappingEligibility?.data.keepMappingAllowed,
		gameInsightsSettings: {
			saveSettings: handleSaveUserWidgetSettings,
			userWidgetSettingsData: userWidgetSettings?.data,
			// We make sure game page are fully loaded before loading the insights
			gamePageIsLoading: gameDataLazyIsLoading || (!!gameData?.data.permissions.useClips && isClipsLoading) || isFetchingUserWidgetSettings,
			events: gameDataLazy?.data?.events,
		},
		showCoachToolbar: false,
		storeDownloadVideoActivity,
		physicalAnalysisReport: {
			isLoading: physicalAnalysisReportIsLoading,
			data: physicalAnalysisReport?.data,
			error: physicalAnalysisReportError,
			handlePostPhysicalAnalysisReportUrls,
		},
		gamePlayerStat: gamePlayerStat?.data,
		gamePlayerStatIsLoading,
		gameTeamStat: gameTeamStat?.data,
		gameTeamStatIsLoading,
		extraAdminOptions:
			gameData?.data.game.videoUploadId != null ? (
				<AdminGameVideoUploadActions videoUploadId={gameData.data.game.videoUploadId} />
			) : undefined,
	});

	if (error) {
		if (error.response) {
			const code = error?.response.status;

			if (code === 404) {
				return (
					<div>
						<NotFoundPage />
					</div>
				);
			}

			if (code === 403) {
				return (
					<div>
						<ForbiddenPage />
					</div>
				);
			}
		}
	}

	return (
		<div className="gamePage">
			{!gameData || gameDataIsLoading ? (
				<GamePageSkeleton />
			) : (
				<>
					<div className="gamePageNavigation-container">
						<span className="icon-new icon-arrow-left icon-14" />
						<Link to="/admin/clubs-amateur">
							<div>{_("my_eyeball.title.clubs")}</div>
							<span className="icon-new icon-arrow-left icon-14" />
						</Link>
						<Link to={`/admin/club-amateur/${gameData.data.homeGameTeam?.club?.id}`}>
							<div>{gameData.data.homeGameTeam?.club?.name}</div>
						</Link>
					</div>

					{gamePage}
				</>
			)}
		</div>
	);
}
